import { createSlice } from "@reduxjs/toolkit";
// ⚠️ Deep-imports the DESKTOP variants on purpose — ledger hazard 6. `userLogout`/
// `supportLogout` are web-only (LoginSlice/index.desktop.ts), and `createBranchPermittedUsersMap`
// exists only in commonHelpers/index.desktop.ts. A bare folder import typechecks against the
// `.native` variant whatever platform file is importing, because packages/core's tsconfig
// sets `moduleSuffixes: [".native", ".shared", ".desktop", ""]`.
import { supportLogout, userLogout } from "../LoginSlice";
import { createBranchPermittedUsersMap } from "../../../helpers/commonHelpers";
import { sharedInitialState, sharedReducers } from "./shared";
import type { SharedTableViewState } from "./shared";

export interface TableViewState extends SharedTableViewState {
  branchUsersMap: Record<string, any>;
  resourceReservationData: Record<string, any>;
  reservationData: any,
  isSubmitted: boolean,
  reservationSettings: any,
}

const initialState: TableViewState = {
  ...sharedInitialState,
  resourceReservationData: {},
  branchUsersMap: {},
  reservationData: [],
  isSubmitted : false,
  reservationSettings: {},
};

export const tableViewSlice = createSlice({
  name: "tableView",
  initialState,
  reducers: {
    ...sharedReducers,
    allTablesCallComplete: (state) => {
      // this action is to tell that the all tables call has completed
    },
    // ⚠️ `getAllResourceGroup`/`getAllResources` take `action` where mobile takes none, and
    // `getUsers` is the reverse. Bodies are identical; only the arity differs, and that is
    // type-level (hazard 14) — web's consumers dispatch these WITH a payload, so the
    // two-param shape must survive here and the one-param shape on mobile.
    getAllResourceGroup: (state, action) => {
      state.isTablesDataFetching = true;
    },
    allResourceGroupCallComplete: (state) => {
      // this action is to tell that the all resource group call has completed
    },
    getAllResources: (state, action) => {
      state.isTablesDataFetching = true;
    },
    allResourcesCallComplete: (state) => {
      // this action is to tell that the all resources call has completed
    },
    getUsers: (state) => {

    },
    saveResourceReservationData: (state, action) => {
      state.resourceReservationData = action.payload || {};
    },
    // ⚠️ Diverges from mobile in payload SHAPE and target field: web destructures
    // `{allUsers, branchId}` and fills `branchUsersMap` (filtered by permitted branch);
    // mobile takes the payload AS the user list and fills `saleUsersMap` (unfiltered).
    getUsersSuccess: (state, action) => {
      const {allUsers, branchId} = action.payload;
      state.users = allUsers;
      state.branchUsersMap = createBranchPermittedUsersMap(allUsers, branchId);
    },
    usersCallComplete: (state) => {
      // this action is to tell that the users call has completed
    },
    printersCallComplete: (state) => {
      // this action is to tell that the printers call has completed
    },
    // `printLabelSlice` / `printLabelSuccess` moved to ./shared 2026-09-12 when the other
    // platform gained the worker that dispatches the first — see shared.ts. They still come out
    // of `tableViewSlice.actions` below, so nothing that imports them changed.
    updateTableViewSlice: (state, action) => {
      return action.payload;
    },
    getTableResveration: (state, action) => {

    },
    saveTableReservation: (state,action) => {
      state.reservationData = action.payload;
    },
    getAllReservationInfo: (state, action) => {
    },
    getResverationSlotData: (state, action) => {
    },
    getResourceReservationData: (state, action) => {
    },
    fetchReservation: (state,action) => {
    },
    reservationSave: (state, action) => {
    },
    cancelReservation: (state,action) => {
    },
    fetchReservationSettings: (state) => {

    },
    saveReservationSettings: (state,action) => {
      state.reservationSettings = action.payload
    },
    feedbackSubmitted: (state, action) => {
      state.isSubmitted = action.payload;
    }
  },
  // ⚠️ Both logout cases are EMPTY in the app and stay empty here. Unlike every other
  // web slice, TableView does NOT reset on logout — the table data survives a user switch.
  // Deliberate-looking or not, it is the current behaviour; the uplift preserves it.
  extraReducers: (builder) => {
    builder
      .addCase(userLogout, (state) => {

      })
      .addCase(supportLogout, (state) => {

      });
  },
});

export const {
  getAllTables,
  getAllTablesSuccess,
  getAllResourceGroup,
  getAllResourceGroupSuccess,
  getAllResources,
  getAllResourcesSuccess,
  getTableOrdersData,
  getTableOrdersDataSuccess,
  transferKOT,
  transferKOTSuccess,
  transferItem,
  transferItemSuccess,
  transferTable,
  transferTableSuccess,
  cancelKOT,
  cancelKOTSuccess,
  cancelOrder,
  cancelOrderSuccess,
  deleteItem,
  deleteItemSuccess,
  splitItems,
  splitItemsSuccess,
  splitOrders,
  splitOrdersSuccess,
  closeOrder,
  closeOrderSuccess,
  calculateSaleFromBE,
  sendDigiatlBillViaEmail,
  sendDigitalBillViaEmailSuccess,
  sendDigiatlBillViaSMS,
  sendDigitalBillViaSMSSuccess,
  sendDigiatlBillViaWA,
  sendDigitalBillViaWASuccess,
  sendAppointmentViaSMS,
  sendAppointmentViaSMSSuccess,
  sendAppointmentViaEmail,
  sendAppointmentViaEmailSuccess,
  sendAppointmentViaWA,
  sendAppointmentViaWASuccess,
  print,
  printSuccess,
  getUsers,
  getUsersSuccess,
  fetchPrinter,
  fetchPrinterSuccess,
  updateCurrentTableInfo,
  emptyTableViewResponses,
  printKOTSlice,
  printKOTSuccess,
  printLabelSlice,
  printLabelSuccess,
  updateTableViewSlice,
  getTableResveration,
  saveTableReservation,
  getAllReservationInfo,
  getResverationSlotData,
  getResourceReservationData,
  saveResourceReservationData,
  fetchReservation,
  reservationSave,
  cancelReservation,
  fetchReservationSettings,
  saveReservationSettings,
  feedbackSubmitted,
  allTablesCallComplete,
  allResourcesCallComplete,
  allResourceGroupCallComplete,
  printersCallComplete,
  usersCallComplete,
} = tableViewSlice.actions;

export default tableViewSlice.reducer;
